import React, { useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import api from "@api";
import DirectoryNavbar from "../Components/navbar/DirectoryNavbar.jsx";
import "./TheField.css";

function TheField() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedSport, setSelectedSport] = useState(
    searchParams.get("sport") || ""
  );

  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedCity, setSelectedCity] = useState(
    searchParams.get("city") || ""
  );

  const [selectedState, setSelectedState] = useState(
    searchParams.get("state") || ""
  );

  const [selectedSkillLevel, setSelectedSkillLevel] =
    useState("");

  const [selectedRating, setSelectedRating] =
    useState("");

  const [selectedPosition, setSelectedPosition] =
    useState("");

  const [openToPlayOnly, setOpenToPlayOnly] =
    useState(false);

  // =========================
  // FETCH PLAYERS
  // =========================

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const res = await api.get(
          "/api/user-profiles-v2"
        );

        setPlayers(res.data.profiles || []);
      } catch (error) {
        console.error(
          "Failed to fetch profiles:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  // =========================
  // RESET SPORT-SPECIFIC FILTERS
  // =========================

  useEffect(() => {
    setSelectedSkillLevel("");
    setSelectedRating("");
    setSelectedPosition("");
    setOpenToPlayOnly(false);
  }, [selectedSport]);

  // =========================
  // SPORTS
  // =========================

  const sports = useMemo(() => {
    const sportMap = new Map();

    players
      .filter((player) => {
        const matchesState =
          !selectedState ||
          player.state === selectedState;

        const matchesCity =
          !selectedCity ||
          player.city === selectedCity;

        return matchesState && matchesCity;
      })
      .forEach((player) => {
        (player.sports || []).forEach((entry) => {
          if (entry.sport) {
            sportMap.set(
              entry.sport.slug,
              entry.sport
            );
          }
        });
      });

    return Array.from(
      sportMap.values()
    ).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [
    players,
    selectedState,
    selectedCity,
  ]);

  // =========================
  // SELECTED SPORT CONFIG
  // =========================

  const selectedSportObject = useMemo(() => {
    return (
      sports.find(
        (sport) =>
          sport.slug === selectedSport
      ) || null
    );
  }, [sports, selectedSport]);

  const selectedSportName =
    selectedSportObject?.name
      ?.toLowerCase()
      .trim() || "";

  const showRatingFilter =
    selectedSportName === "tennis" ||
    selectedSportName === "pickleball";

  const showPositionFilter = [
    "basketball",
    "football",
    "flag football",
    "soccer",
    "baseball",
    "softball",
    "volleyball",
  ].includes(selectedSportName);

  // =========================
  // STATES
  // =========================

  const states = useMemo(() => {
    return [
      ...new Set(
        players
          .filter((player) => {
            const matchesSport =
              !selectedSport ||
              (player.sports || []).some(
                (entry) =>
                  entry.sport?.slug ===
                  selectedSport
              );

            const matchesCity =
              !selectedCity ||
              player.city === selectedCity;

            return (
              matchesSport &&
              matchesCity
            );
          })
          .map((player) => player.state)
          .filter(Boolean)
      ),
    ].sort();
  }, [
    players,
    selectedSport,
    selectedCity,
  ]);

  // =========================
  // CITIES
  // =========================

  const cities = useMemo(() => {
    return [
      ...new Set(
        players
          .filter((player) => {
            const matchesSport =
              !selectedSport ||
              (player.sports || []).some(
                (entry) =>
                  entry.sport?.slug ===
                  selectedSport
              );

            const matchesState =
              !selectedState ||
              player.state === selectedState;

            return (
              matchesSport &&
              matchesState
            );
          })
          .map((player) => player.city)
          .filter(Boolean)
      ),
    ].sort();
  }, [
    players,
    selectedSport,
    selectedState,
  ]);

  // =========================
  // SKILL LEVELS
  // =========================

  const skillLevels = useMemo(() => {
    const values = new Set();

    players.forEach((player) => {
      (player.sports || []).forEach(
        (entry) => {
          if (
            entry.sport?.slug ===
            selectedSport &&
            entry.skill_level
          ) {
            values.add(entry.skill_level);
          }
        }
      );
    });

    const order = [
      "Beginner",
      "Intermediate",
      "Advanced",
    ];

    return Array.from(values).sort(
      (a, b) =>
        order.indexOf(a) -
        order.indexOf(b)
    );
  }, [players, selectedSport]);

  // =========================
  // RATINGS
  // =========================

  const ratings = useMemo(() => {
    const values = new Set();

    players.forEach((player) => {
      (player.sports || []).forEach(
        (entry) => {
          if (
            entry.sport?.slug ===
            selectedSport &&
            entry.rating
          ) {
            values.add(entry.rating);
          }
        }
      );
    });

    return Array.from(values).sort(
      (a, b) => {
        const aNumber = parseFloat(a);
        const bNumber = parseFloat(b);

        if (
          Number.isNaN(aNumber) ||
          Number.isNaN(bNumber)
        ) {
          return String(a).localeCompare(
            String(b)
          );
        }

        return aNumber - bNumber;
      }
    );
  }, [players, selectedSport]);

  // =========================
  // POSITIONS
  // =========================

  const positions = useMemo(() => {
    const values = new Set();

    players.forEach((player) => {
      (player.sports || []).forEach(
        (entry) => {
          if (
            entry.sport?.slug ===
            selectedSport &&
            entry.position
          ) {
            values.add(entry.position);
          }
        }
      );
    });

    return Array.from(values).sort();
  }, [players, selectedSport]);

  // =========================
  // FILTER PLAYERS
  // =========================

  const filteredPlayers = useMemo(() => {
    const term =
      searchTerm.trim().toLowerCase();

    return players.filter((player) => {
      const fullName =
        `${player.first_name || ""} ${player.last_name || ""
          }`.toLowerCase();

      const location =
        `${player.city || ""} ${player.state || ""
          }`.toLowerCase();

      const bio =
        (player.bio || "").toLowerCase();

      const matchesSearch =
        !term ||
        fullName.includes(term) ||
        location.includes(term) ||
        bio.includes(term);

      const selectedSportEntry =
        selectedSport
          ? (player.sports || []).find(
            (entry) =>
              entry.sport?.slug ===
              selectedSport
          )
          : null;

      const matchesSport =
        !selectedSport ||
        Boolean(selectedSportEntry);

      const matchesCity =
        !selectedCity ||
        player.city === selectedCity;

      const matchesState =
        !selectedState ||
        player.state === selectedState;

      const matchesSkillLevel =
        !selectedSkillLevel ||
        selectedSportEntry?.skill_level ===
        selectedSkillLevel;

      const matchesRating =
        !selectedRating ||
        selectedSportEntry?.rating ===
        selectedRating;

      const matchesPosition =
        !selectedPosition ||
        selectedSportEntry?.position ===
        selectedPosition;

      const matchesOpenToPlay =
        !openToPlayOnly ||
        selectedSportEntry?.looking_to_play ===
        true;

      return (
        matchesSearch &&
        matchesSport &&
        matchesCity &&
        matchesState &&
        matchesSkillLevel &&
        matchesRating &&
        matchesPosition &&
        matchesOpenToPlay
      );
    });
  }, [
    players,
    searchTerm,
    selectedSport,
    selectedCity,
    selectedState,
    selectedSkillLevel,
    selectedRating,
    selectedPosition,
    openToPlayOnly,
  ]);

  // =========================
  // RESET
  // =========================

  const handleReset = () => {
    setSearchTerm("");
    setSelectedCity("");
    setSelectedState("");
    setSelectedSport("");
    setSelectedSkillLevel("");
    setSelectedRating("");
    setSelectedPosition("");
    setOpenToPlayOnly(false);

    setSearchParams({});
  };

  // =========================
  // HELPERS
  // =========================

  const getInitials = (player) => {
    return `${player.first_name?.charAt(0) || ""}${player.last_name?.charAt(0) || ""
      }`.toUpperCase();
  };

  const getNodeClass = (id, index) => {
    const number = Number(id) || index;

    const sizes = [
      "field-node-small",
      "field-node-medium",
      "field-node-large",
      "field-node-medium",
      "field-node-small",
      "field-node-large",
    ];

    return sizes[
      number % sizes.length
    ];
  };

  const getNodeStyle = (id, index) => {
    const number =
      Number(id) || index + 1;

    const xPattern = [
      3, 8, -4, 12, -10, 5, 14, -7,
      2, 10, -12, 6, -2, 13, -8, 4,
    ];

    const yPattern = [
      0, 22, -12, 10, 28, -18, 16,
      4, -22, 20, 7, -10, 26, -16,
      12, 2,
    ];

    return {
      "--field-x": `${xPattern[
        number % xPattern.length
        ]
        }px`,

      "--field-y": `${yPattern[
        number % yPattern.length
        ]
        }px`,
    };
  };

  // =========================
  // LOADING
  // =========================

  if (loading) {
    return (
      <div className="field-page">
        <DirectoryNavbar />

        <main className="field-container">
          <div className="field-loading">
            Loading The Field...
          </div>
        </main>
      </div>
    );
  }

  // =========================
  // RENDER
  // =========================

  return (
    <div className="field-page">
      <DirectoryNavbar />

      <main className="field-container">

        <div className="arena-controls">

          {/* Search */}
          <div className="field-search">
            <Search size={18} />

            <input
              type="text"
              placeholder="Who's in The Arena?"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
            />
          </div>

          {/* Filters */}
          <div className="arena-filter-row">

            {/* Sport */}
            <select
              className={selectedSport ? "filter-active" : ""}
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
            >
              <option value="">
                All Sports
              </option>

              {sports.map((sport) => (
                <option
                  key={sport.id}
                  value={sport.slug}
                >
                  {sport.name}
                </option>
              ))}
            </select>

            {/* State */}
            <select
              className={selectedState ? "filter-active" : ""}
              value={selectedState}
              onChange={(e) =>
                setSelectedState(
                  e.target.value
                )
              }
            >
              <option value="">
                All States
              </option>

              {states.map((state) => (
                <option
                  key={state}
                  value={state}
                >
                  {state}
                </option>
              ))}
            </select>

            {/* City */}
            <select
              className={selectedCity ? "filter-active" : ""}
              value={selectedCity}
              onChange={(e) =>
                setSelectedCity(
                  e.target.value
                )
              }
            >
              <option value="">
                All Cities
              </option>

              {cities.map((city) => (
                <option
                  key={city}
                  value={city}
                >
                  {city}
                </option>
              ))}
            </select>

            {/* Level */}
            {selectedSport && (
              <select
                className={selectedSkillLevel ? "filter-active" : ""}
                value={
                  selectedSkillLevel
                }
                onChange={(e) =>
                  setSelectedSkillLevel(
                    e.target.value
                  )
                }
              >
                <option value="">
                  All Levels
                </option>

                {skillLevels.map(
                  (level) => (
                    <option
                      key={level}
                      value={level}
                    >
                      {level}
                    </option>
                  )
                )}
              </select>
            )}

            {/* Rating */}
            {selectedSport &&
              showRatingFilter && (
                <select
                className={selectedRating ? "filter-active" : ""}
                  value={
                    selectedRating
                  }
                  onChange={(e) =>
                    setSelectedRating(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    All Ratings
                  </option>

                  {ratings.map(
                    (rating) => (
                      <option
                        key={rating}
                        value={rating}
                      >
                        {rating}
                      </option>
                    )
                  )}
                </select>
              )}

            {/* Position */}
            {selectedSport &&
              showPositionFilter && (
                <select
                className={selectedPosition ? "filter-active" : ""}
                  value={
                    selectedPosition
                  }
                  onChange={(e) =>
                    setSelectedPosition(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    All Positions
                  </option>

                  {positions.map(
                    (position) => (
                      <option
                        key={position}
                        value={position}
                      >
                        {position}
                      </option>
                    )
                  )}
                </select>
              )}

            {/* Open to Play */}
            {selectedSport && (
              <label className="arena-open-to-play-filter">
                <input
                  className={`arena-open-to-play-filter ${openToPlayOnly ? "filter-active" : ""
                    }`}
                  type="checkbox"
                  checked={
                    openToPlayOnly
                  }
                  onChange={(e) =>
                    setOpenToPlayOnly(
                      e.target.checked
                    )
                  }
                />

                <span>
                  Open to play
                </span>
              </label>
            )}

            {/* Reset */}
            <button
              type="button"
              className="arena-reset-button"
              onClick={handleReset}
            >
              Reset
            </button>

          </div>
        </div>

        {/* Empty */}
        {filteredPlayers.length === 0 ? (
          <div className="field-empty">
            <h2>No players found</h2>

            <p>
              Try another search or
              filter.
            </p>
          </div>
        ) : (

          /* Players */
          <section className="field-universe">

            {filteredPlayers.map(
              (player, index) => (
                <button
                  key={player.id}
                  type="button"
                  className={`field-node ${getNodeClass(
                    player.id,
                    index
                  )}`}
                  style={getNodeStyle(
                    player.id,
                    index
                  )}
                  onClick={() =>
                    navigate(
                      `/profile/${player.id}`
                    )
                  }
                >

                  {/* Avatar */}
                  <div className="field-node-avatar">

                    {player.profile_image ? (
                      <img
                        src={
                          player.profile_image
                        }
                        alt={`${player.first_name || ""} ${player.last_name || ""
                          }`}
                      />
                    ) : (
                      <div className="field-node-fallback">
                        {getInitials(
                          player
                        )}
                      </div>
                    )}

                  </div>

                  {/* Name */}
                  <div className="field-node-name">
                    {player.first_name}{" "}
                    {player.last_name}
                  </div>

                  {/* Location */}
                  {(player.city ||
                    player.state) && (
                      <div className="field-node-location">
                        <MapPin
                          size={11}
                        />

                        <span>
                          {[
                            player.city,
                            player.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    )}

                  {/* Hover */}
                  <div className="field-node-hover">

                    <strong>
                      {player.first_name}{" "}
                      {player.last_name}
                    </strong>

                    {(player.city ||
                      player.state) && (
                        <span>
                          {[
                            player.city,
                            player.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}

                    {player.bio && (
                      <p>
                        {player.bio}
                      </p>
                    )}

                  </div>

                </button>
              )
            )}

          </section>
        )}

      </main>
    </div>
  );
}

export default TheField;